/**
 * Search Drawer - Full Experience Controller
 * - Multi-view flow (search, deep results, detail sheet)
 * - Predictive search data via /search/suggest + predictive-search section
 * - GSAP transitions for view, list, and detail interactions
 */
(function() {
  'use strict';

  const DEBOUNCE_DELAY = 320;
  const MIN_QUERY_LENGTH = 2;
  const RECENT_KEY = 'untamed_search_recent';

  class SearchDrawer {
    constructor() {
      this.drawer = document.querySelector('[data-search-drawer]');
      if (!this.drawer) return;

      this.overlay = this.drawer.querySelector('[data-search-overlay]');
      this.content = this.drawer.querySelector('.search-drawer__content');
      this.closeBtn = this.drawer.querySelector('[data-search-close]');
      this.input = this.drawer.querySelector('[data-search-input]');
      this.searchIcon = this.drawer.querySelector('.search-drawer__search-icon');
      this.clearBtn = this.drawer.querySelector('[data-search-clear]');
      this.tabsWrap = this.drawer.querySelector('[data-search-tabs]');
      this.tabButtons = Array.from(this.drawer.querySelectorAll('[data-search-tab]'));
      this.tabCountEls = {
        products: this.drawer.querySelector('[data-search-tab-count="products"]'),
        collections: this.drawer.querySelector('[data-search-tab-count="collections"]'),
        blogs: this.drawer.querySelector('[data-search-tab-count="blogs"]')
      };
      this.idlePanel = this.drawer.querySelector('[data-search-idle]');
      this.predictivePanel = this.drawer.querySelector('[data-search-predictive]');
      this.primaryBlock = this.drawer.querySelector('[data-search-primary-block]');
      this.primaryTitle = this.drawer.querySelector('[data-search-primary-title]');
      this.primaryList = this.drawer.querySelector('[data-search-primary-list]');
      this.quickBlock = this.drawer.querySelector('[data-search-quick-block]');
      this.quickList = this.drawer.querySelector('[data-search-quick-list]');
      this.categoriesBlock = this.drawer.querySelector('[data-search-categories-block]');
      this.categoriesList = this.drawer.querySelector('[data-search-categories-list]');
      this.suggestionsWrap = this.drawer.querySelector('[data-search-suggestions-wrap]');
      this.suggestionsList = this.drawer.querySelector('[data-search-suggestions]');
      this.tabHeading = this.drawer.querySelector('[data-search-active-heading]');
      this.tabCount = this.drawer.querySelector('[data-search-active-count]');
      this.tabResults = this.drawer.querySelector('[data-search-tab-results]');
      this.emptyState = this.drawer.querySelector('[data-search-empty]');
      this.clearHistoryBtn = this.drawer.querySelector('[data-search-clear-history]');
      this.resetBtn = this.drawer.querySelector('[data-search-reset]');
      this.openResultsBtn = this.drawer.querySelector('[data-search-open-results]');
      this.fullSearchLink = this.drawer.querySelector('[data-search-full-link]');
      this.config = this.readDrawerConfig();

      this.state = {
        isOpen: false,
        isLoading: false,
        query: '',
        activeTab: 'products',
        recent: this.loadRecentSearches(),
        results: {
          products: [],
          collections: [],
          blogs: []
        },
        suggestions: []
      };

      this.requestCounter = 0;
      this.activeRequestId = 0;
      this.debounceTimer = null;
      this.abortController = null;
      this.previousBodyOverflow = '';

      this.init();
    }

    init() {
      this.renderQuickSearches();
      this.renderCategoryCards();
      this.renderPrimaryHistorySection();
      this.showIdlePanel();
      this.updateFullSearchLink('');

      this.overlay?.addEventListener('click', () => this.close());
      this.closeBtn?.addEventListener('click', () => this.close());
      this.clearBtn?.addEventListener('click', () => this.clearInput(true));
      this.clearHistoryBtn?.addEventListener('click', () => this.clearHistory());
      this.resetBtn?.addEventListener('click', () => this.clearInput(true));
      this.openResultsBtn?.addEventListener('click', () => this.openResultsView());

      this.input?.addEventListener('input', () => this.onInputChange());
      this.input?.addEventListener('keydown', (event) => this.onInputKeydown(event));

      this.tabButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const tab = button.dataset.searchTab;
          if (!tab) return;
          this.switchTab(tab);
        });
      });

      this.drawer.addEventListener('click', (event) => this.onDrawerClick(event));
      window.addEventListener('search:open-drawer', () => this.open());
      document.addEventListener('keydown', (event) => this.onDocumentKeydown(event));
    }

    onDocumentKeydown(event) {
      if (event.key !== 'Escape' || !this.state.isOpen) return;
      this.close();
    }

    onDrawerClick(event) {
      const searchQueryEl = event.target.closest('[data-search-primary-query], [data-search-quick-query], [data-search-category-query], [data-search-suggestion-query], [data-search-empty-action]');
      if (searchQueryEl) {
        const query = searchQueryEl.dataset.searchPrimaryQuery
          || searchQueryEl.dataset.searchQuickQuery
          || searchQueryEl.dataset.searchCategoryQuery
          || searchQueryEl.dataset.searchSuggestionQuery
          || searchQueryEl.dataset.query
          || '';
        if (query) {
          this.applyQuery(query);
        }
        return;
      }

      const resultRow = event.target.closest('[data-search-result-action]');
      if (resultRow) {
        const kind = resultRow.dataset.itemKind;
        const itemId = resultRow.dataset.itemId;
        const item = this.getItemById(kind, itemId);
        if (!item) return;

        if (item.url) {
          window.location.href = item.url;
        }
        return;
      }
    }

    onInputChange() {
      const query = this.input ? this.input.value.trim() : '';
      this.state.query = query;
      if (this.clearBtn) this.clearBtn.hidden = query.length === 0;
      this.updateFullSearchLink(query);

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.activeRequestId = 0;
      this.abortController?.abort();
      this.hideSpinner();

      if (query.length < MIN_QUERY_LENGTH) {
        this.requestCounter += 1;
        this.state.results = { products: [], collections: [], blogs: [] };
        this.state.suggestions = [];
        this.state.query = '';

        this.showIdlePanel();
        return;
      }

      this.showSpinner();
      this.debounceTimer = setTimeout(() => {
        this.performSearch(query);
      }, DEBOUNCE_DELAY);
    }

    onInputKeydown(event) {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      const query = this.input ? this.input.value.trim() : '';
      if (!query) return;

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.performSearch(query, { immediate: true });
    }

    open() {
      if (this.state.isOpen) return;
      this.state.isOpen = true;
      this.drawer.setAttribute('aria-hidden', 'false');
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.body.classList.add('search-drawer-open');

      const hasQuery = Boolean((this.input && this.input.value.trim()) || (this.state.query || '').trim());
      if (!hasQuery) {
        this.showIdlePanel();
      }

      this.refreshIcons();
      this.focusInput();
    }

    close() {
      if (!this.state.isOpen) return;
      this.state.isOpen = false;
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('search-drawer-open');
      document.body.style.overflow = this.previousBodyOverflow || '';

      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.abortController?.abort();
      this.clearInput(false);
    }

    clearInput(shouldFocus) {
      this.state.query = '';
      this.state.results = { products: [], collections: [], blogs: [] };
      this.state.suggestions = [];
      this.state.activeTab = 'products';

      if (this.input) this.input.value = '';
      if (this.clearBtn) this.clearBtn.hidden = true;
      this.hideSpinner();
      this.updateFullSearchLink('');
      this.updateTabSelection();
      this.showIdlePanel();

      if (shouldFocus) this.focusInput();
    }

    focusInput() {
      setTimeout(() => {
        if (!this.state.isOpen) return;
        this.input?.focus();
      }, 80);
    }

    showSpinner() {
      this.state.isLoading = true;
      if (this.searchIcon) this.searchIcon.hidden = false;
      this.renderLoadingState();
    }

    hideSpinner() {
      this.state.isLoading = false;
      if (this.searchIcon) this.searchIcon.hidden = false;
    }

    showIdlePanel() {
      this.state.isLoading = false;
      if (this.tabsWrap) this.tabsWrap.hidden = true;
      if (this.idlePanel) this.idlePanel.hidden = false;
      if (this.predictivePanel) this.predictivePanel.hidden = true;
      if (this.emptyState) this.emptyState.hidden = true;
      if (this.tabResults) this.tabResults.innerHTML = '';
      this.renderPrimaryHistorySection();
      this.renderQuickSearches();
      this.renderCategoryCards();
    }

    showPredictivePanel() {
      if (this.tabsWrap) this.tabsWrap.hidden = false;
      if (this.idlePanel) this.idlePanel.hidden = true;
      if (this.predictivePanel) this.predictivePanel.hidden = false;
    }

    switchTab(tab) {
      if (!tab || tab === this.state.activeTab) return;
      this.state.activeTab = tab;
      this.updateTabSelection();
      this.renderActiveTab();
    }

    updateTabSelection() {
      this.tabButtons.forEach((button) => {
        button.classList.toggle('is-active', button.dataset.searchTab === this.state.activeTab);
      });
    }

    async performSearch(query, options) {
      const opts = options || {};
      const requestId = ++this.requestCounter;
      this.activeRequestId = requestId;
      this.state.query = query;
      this.updateFullSearchLink(query);
      this.showSpinner();

      this.abortController?.abort();
      this.abortController = new AbortController();

      try {
        const url = new URL('/search/suggest', window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('section_id', 'predictive-search');
        url.searchParams.set('resources[type]', 'product,collection,page,article');
        url.searchParams.set('resources[limit]', '8');
        url.searchParams.set('resources[options][unavailable_products]', 'last');

        const response = await fetch(url.toString(), {
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error('Predictive search request failed');
        }

        const html = await response.text();
        if (requestId !== this.requestCounter) return;
        const currentInput = this.input ? this.input.value.trim() : '';
        if (currentInput !== query) return;

        const parsed = this.parsePredictiveResponse(html, query);
        this.state.results = parsed.results;
        this.state.suggestions = parsed.suggestions;
        this.state.isLoading = false;
        this.renderPredictiveState();

      } catch (error) {
        if (error && error.name === 'AbortError') return;
        console.error('Search drawer predictive search failed:', error);
        this.showIdlePanel();
      } finally {
        if (this.activeRequestId === requestId) this.hideSpinner();
      }
    }

    renderLoadingState() {
      if (!this.tabResults) return;
      this.showPredictivePanel();
      if (this.tabsWrap) this.tabsWrap.hidden = false;
      if (this.emptyState) this.emptyState.hidden = true;
      this.updateTabSelection();

      this.tabResults.innerHTML = Array.from({ length: 4 }).map(() => `
        <div class="search-drawer__result-row product-card-horizontal product-card-horizontal--search-drawer search-drawer__result-row--skeleton" aria-hidden="true">
          <span class="product-card-horizontal__media product-card-horizontal__media--skeleton"></span>
          <span class="product-card-horizontal__content">
            <span class="product-card-horizontal__top">
              <span class="product-card-horizontal__head">
                <span class="search-drawer__skeleton-line search-drawer__skeleton-line--title"></span>
                <span class="search-drawer__skeleton-line search-drawer__skeleton-line--price"></span>
              </span>
              <span class="search-drawer__skeleton-line search-drawer__skeleton-line--meta"></span>
            </span>
            <span class="product-card-horizontal__bottom">
              <span class="search-drawer__skeleton-chip-row">
                <span class="search-drawer__skeleton-chip"></span>
                <span class="search-drawer__skeleton-chip"></span>
                <span class="search-drawer__skeleton-chip"></span>
              </span>
              <span class="search-drawer__skeleton-line search-drawer__skeleton-line--rating"></span>
            </span>
          </span>
        </div>
      `).join('');
    }

    parsePredictiveResponse(html, query) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const products = [];
      const collections = [];
      const blogs = [];
      const term = (query || '').toLowerCase();
      const uniqueSuggestions = new Set();

      const productNodes = doc.querySelectorAll('[data-section="products"] [data-product-card-horizontal]');
      productNodes.forEach((node, index) => {
        const id = String(node.dataset.productId || index + 1);
        const renderMode = this.sanitizeClassToken(node.dataset.productCardHorizontalMode || 'default');
        const title = (node.dataset.productTitle || this.readText(node, '.product-card-horizontal__title') || 'Product').trim();
        const subtitle = (node.dataset.productSubtitle || this.readText(node, '.product-card-horizontal__subtitle') || 'Product details').trim();
        const priceText = node.dataset.productPriceText || this.readText(node, '.product-card-horizontal__price');
        const comparePriceText = node.dataset.productComparePriceText || this.readText(node, '.product-card-horizontal__compare-price');
        const priceValue = this.parsePrice(priceText);
        const comparePriceValue = this.parsePrice(comparePriceText);
        const image = node.dataset.productImage || this.readAttr(node, '.product-card-horizontal__image', 'src');
        const url = node.dataset.productUrl || this.readAttr(node, '.product-card-horizontal__media-link', 'href');
        // Reuse the shared badge markup rendered by snippets/badge-list.liquid.
        const badgesEl = node.querySelector('.product-card-horizontal__badges');
        const badgesHtml = badgesEl ? badgesEl.outerHTML : '';
        const ratingEnabled = this.parseBoolean(node.dataset.productRatingEnabled);
        const ratingHidden = this.parseBoolean(node.dataset.productRatingHidden);
        const ratingDisplay = (node.dataset.productRatingDisplay || 'star_rating_count').trim();
        const ratingSize = (node.dataset.productRatingSize || 'sm').trim();
        const ratingFontWeight = (node.dataset.productRatingFontWeight || '400').trim();
        const ratingValue = this.toNumber(node.dataset.productRating);
        const reviewCount = this.toInteger(node.dataset.productReviews);
        const reviewsLabel = (node.dataset.productReviewsLabel || '').trim();
        const stockState = node.dataset.productStockState || '';
        const stockText = node.dataset.productStockText || '';
        const available = this.parseBoolean(node.dataset.productAvailable);
        const onSale = this.parseBoolean(node.dataset.productOnSale) || Boolean(comparePriceText);
        const swatchOverflow = this.toInteger(node.dataset.productSwatchOverflow);
        const swatches = Array.from(node.querySelectorAll('.product-card-horizontal__swatch'))
          .map((swatch) => (swatch.dataset.colorLabel || '').trim())
          .filter((value) => value !== '');

        if (title) uniqueSuggestions.add(title);

        products.push({
          id,
          kind: 'product',
          renderMode,
          title: title || 'Product',
          subtitle: subtitle || 'Product details',
          meta: subtitle || 'Product details',
          priceText: priceText || '',
          comparePriceText: comparePriceText || '',
          onSale,
          priceValue,
          comparePriceValue,
          image: image || '',
          url: url || '',
          badgesHtml,
          ratingEnabled,
          ratingHidden,
          ratingDisplay,
          ratingSize,
          ratingFontWeight,
          ratingValue: Number.isFinite(ratingValue) ? ratingValue : 0,
          reviewCount: Number.isFinite(reviewCount) ? reviewCount : 0,
          reviewsLabel: reviewsLabel || '',
          stockState,
          stockText,
          available,
          swatches,
          swatchOverflow: Number.isFinite(swatchOverflow) ? swatchOverflow : 0
        });
      });

      const collectionNodes = doc.querySelectorAll('[data-section="collections"] .predictive-search__collection-item');
      collectionNodes.forEach((node, index) => {
        const id = String(index + 1);
        const title = this.readText(node, '.predictive-search__collection-title');
        const meta = this.readText(node, '.predictive-search__collection-products');
        const image = this.readAttr(node, '.predictive-search__collection-image', 'src');
        const url = node.getAttribute('href') || '';

        if (title) uniqueSuggestions.add(title);

        collections.push({
          id,
          kind: 'collection',
          title: title || 'Collection',
          meta: meta || 'Collection',
          image: image || '',
          url
        });
      });

      const blogNodes = doc.querySelectorAll('[data-section="pages"] .predictive-search__page-item');
      blogNodes.forEach((node, index) => {
        const id = String(index + 1);
        const title = this.readText(node, '.predictive-search__page-title');
        const meta = this.readText(node, '.predictive-search__page-meta');
        const url = node.getAttribute('href') || '';

        if (title) uniqueSuggestions.add(title);

        blogs.push({
          id,
          kind: 'blog',
          title: title || 'Article',
          meta: meta || 'Inspiration',
          image: '',
          url
        });
      });

      const filteredSuggestions = Array.from(uniqueSuggestions)
        .filter((value) => value.toLowerCase().includes(term))
        .slice(0, 6);

      if (filteredSuggestions.length === 0 && query) {
        filteredSuggestions.push(query);
      }

      return {
        results: {
          products,
          collections,
          blogs
        },
        suggestions: filteredSuggestions
      };
    }

    renderPredictiveState() {
      this.showPredictivePanel();
      if (this.tabsWrap) {
        this.tabsWrap.hidden = this.getTotalResultCount() === 0;
      }

      this.updateTabCounts();
      this.updateTabSelection();
      this.renderSuggestions();
      this.renderActiveTab();
      this.refreshIcons();
    }

    renderSuggestions() {
      const suggestions = this.state.suggestions || [];
      if (!this.suggestionsWrap || !this.suggestionsList) return;

      if (!suggestions.length) {
        this.suggestionsWrap.hidden = true;
        this.suggestionsList.innerHTML = '';
        return;
      }

      this.suggestionsWrap.hidden = false;
      this.suggestionsList.innerHTML = suggestions
        .map((term) => `
          <button type="button" class="search-drawer__suggestion-row" data-search-suggestion-query="${this.escapeAttr(term)}">
            <span>${this.escapeHtml(term)}</span>
            <span class="ph-bold ph-arrow-up-right"></span>
          </button>
        `)
        .join('');

      this.animateIn(this.suggestionsList.querySelectorAll('.search-drawer__suggestion-row'), { x: -10, stagger: 0.04 });
    }

    renderActiveTab() {
      if (!this.tabResults || !this.tabHeading || !this.tabCount) return;
      if (this.state.isLoading) {
        this.renderLoadingState();
        return;
      }

      const tab = this.state.activeTab;
      const list = this.getListByTab(tab);
      const headingMap = {
        products: 'Matching products',
        collections: 'Matching collections',
        blogs: 'Matching blogs & ideas'
      };
      const countLabelMap = {
        products: 'items',
        collections: 'collections',
        blogs: 'posts'
      };

      this.tabHeading.textContent = headingMap[tab] || 'Matches';
      this.tabCount.textContent = `${list.length} ${countLabelMap[tab] || 'items'}`;

      if (!list.length) {
        this.tabResults.innerHTML = '';
        if (this.emptyState) this.emptyState.hidden = false;
        return;
      }

      if (this.emptyState) this.emptyState.hidden = true;

      this.tabResults.innerHTML = list
        .map((item) => this.renderResultRow(item))
        .join('');

      this.animateIn(this.tabResults.querySelectorAll('.search-drawer__result-row'), { y: 8, stagger: 0.03 });
    }

    renderResultRow(item) {
      if (item && item.kind === 'product') {
        return this.renderHorizontalProductRow(item, 'predictive');
      }

      const media = item.image
        ? `<img src="${this.escapeAttr(item.image)}" alt="${this.escapeAttr(item.title)}">`
        : '<span class="ph-bold ph-magnifying-glass"></span>';
      const price = item.priceText ? `<span class="search-drawer__result-price">${this.escapeHtml(item.priceText)}</span>` : '';
      const kind = this.escapeAttr(item.kind);
      const itemId = this.escapeAttr(item.id);

      return `
        <button type="button" class="search-drawer__result-row" data-search-result-action="open-item" data-item-kind="${kind}" data-item-id="${itemId}">
          <span class="search-drawer__result-media">${media}</span>
          <span class="search-drawer__result-content">
            <span class="search-drawer__result-title">${this.escapeHtml(item.title)}</span>
            <span class="search-drawer__result-meta">${this.escapeHtml(item.meta || '')}</span>
            ${price}
          </span>
        </button>
      `;
    }

    renderHorizontalProductRow(item, context) {
      const kind = this.escapeAttr(item.kind || 'product');
      const itemId = this.escapeAttr(item.id || '');
      const title = this.escapeHtml(item.title || 'Product');
      const subtitle = this.escapeHtml(item.subtitle || item.meta || 'Product details');
      const media = item.image
        ? `<img src="${this.escapeAttr(item.image)}" alt="${this.escapeAttr(item.title)}" class="product-card-horizontal__image">`
        : '<span class="product-card-horizontal__placeholder"><span class="ph-bold ph-package"></span></span>';
      const swatchOrStock = this.renderSwatchOrStockState(item);
      const comparePrice = item.comparePriceText
        ? `<span class="product-card-horizontal__compare-price">${this.escapeHtml(item.comparePriceText)}</span>`
        : '';
      const priceClass = item.onSale ? 'product-card-horizontal__price product-card-horizontal__price--sale' : 'product-card-horizontal__price';
      const surfaceClass = context === 'results' ? 'product-card-horizontal--results' : 'product-card-horizontal--predictive';
      const renderMode = this.sanitizeClassToken(item.renderMode || 'default');
      const ratingMarkup = this.renderRatingMarkup(item);
      // Pre-rendered shared badge markup carried over from the server node (trusted theme HTML).
      const badgeRowMarkup = item.badgesHtml || '';

      return `
        <button type="button" class="search-drawer__result-row product-card-horizontal product-card-horizontal--search-drawer product-card-horizontal--mode-${renderMode} ${surfaceClass}" data-search-result-action="open-item" data-item-kind="${kind}" data-item-id="${itemId}">
          <span class="product-card-horizontal__media">
            ${media}
          </span>
          <span class="product-card-horizontal__content">
            <span class="product-card-horizontal__top">
              ${badgeRowMarkup}
              <span class="product-card-horizontal__head">
                <span class="product-card-horizontal__title">${title}</span>
                <span class="product-card-horizontal__price-row">
                  <span class="${priceClass}">${this.escapeHtml(item.priceText || '')}</span>
                  ${comparePrice}
                </span>
              </span>
              <span class="product-card-horizontal__subtitle">${subtitle}</span>
            </span>
            <span class="product-card-horizontal__bottom">
              ${swatchOrStock}
              ${ratingMarkup}
            </span>
          </span>
        </button>
      `;
    }

    updateTabCounts() {
      if (this.tabCountEls.products) this.tabCountEls.products.textContent = String(this.state.results.products.length);
      if (this.tabCountEls.collections) this.tabCountEls.collections.textContent = String(this.state.results.collections.length);
      if (this.tabCountEls.blogs) this.tabCountEls.blogs.textContent = String(this.state.results.blogs.length);
    }

    openResultsView() {
      const query = (this.state.query || '').trim();
      if (query) this.addRecentSearch(query);
      window.location.href = this.buildFullSearchUrl(query);
    }

    renderPrimaryHistorySection() {
      if (!this.primaryBlock || !this.primaryTitle || !this.primaryList) return;

      const recentItems = (this.state.recent || []).slice(0, 6);
      if (recentItems.length > 0) {
        this.primaryBlock.hidden = false;
        this.primaryTitle.textContent = 'Recent searches';
        if (this.clearHistoryBtn) this.clearHistoryBtn.hidden = false;
        this.primaryList.innerHTML = recentItems
          .map((query) => `
            <button type="button" class="search-drawer__recent-row" data-search-primary-query="${this.escapeAttr(query)}">
              <span>${this.escapeHtml(query)}</span>
              <span class="ph-bold ph-arrow-up-right"></span>
            </button>
          `)
          .join('');
        return;
      }

      const trendingItems = this.config.trending;
      if (trendingItems.length === 0) {
        this.primaryList.innerHTML = '';
        this.primaryBlock.hidden = true;
        if (this.clearHistoryBtn) this.clearHistoryBtn.hidden = true;
        return;
      }

      this.primaryBlock.hidden = false;
      this.primaryTitle.textContent = 'Trending searches';
      if (this.clearHistoryBtn) this.clearHistoryBtn.hidden = true;
      this.primaryList.innerHTML = trendingItems
        .map((item) => this.renderPrimaryRow(item))
        .join('');
    }

    renderPrimaryRow(item) {
      const text = this.escapeHtml(item.text);
      const link = this.escapeAttr(item.link || '');

      if (link) {
        return `
          <a class="search-drawer__recent-row search-drawer__recent-row--link" href="${link}">
            <span>${text}</span>
            <span class="ph-bold ph-arrow-up-right"></span>
          </a>
        `;
      }

      return `
        <button type="button" class="search-drawer__recent-row" data-search-primary-query="${this.escapeAttr(item.text)}">
          <span>${text}</span>
          <span class="ph-bold ph-arrow-up-right"></span>
        </button>
      `;
    }

    renderQuickSearches() {
      if (!this.quickBlock || !this.quickList) return;

      const quickItems = this.config.quickSearches;
      if (quickItems.length === 0) {
        this.quickList.innerHTML = '';
        this.quickBlock.hidden = true;
        return;
      }

      this.quickBlock.hidden = false;
      this.quickList.innerHTML = quickItems
        .map((item) => {
          const text = this.escapeHtml(item.text);
          const link = this.escapeAttr(item.link || '');
          if (link) {
            return `<a class="search-drawer__pill" href="${link}">${text}</a>`;
          }
          return `<button type="button" class="search-drawer__pill" data-search-quick-query="${this.escapeAttr(item.text)}">${text}</button>`;
        })
        .join('');
    }

    renderCategoryCards() {
      if (!this.categoriesBlock || !this.categoriesList) return;

      const categoryItems = (this.config.categories || []).filter((item) => item.text);
      if (categoryItems.length === 0) {
        this.categoriesList.innerHTML = '';
        this.categoriesBlock.hidden = true;
        return;
      }

      this.categoriesBlock.hidden = false;
      this.categoriesList.innerHTML = categoryItems
        .map((item) => {
          const text = this.escapeHtml(item.text);
          const link = this.escapeAttr(item.link || '');
          const image = this.escapeAttr(item.image || '');
          const media = image
            ? `<img src="${image}" alt="${this.escapeAttr(item.text)}">`
            : `<span class="search-drawer__department-placeholder" aria-hidden="true">${this.config.categoryPlaceholderSvg || ''}</span>`;
          if (link) {
            return `
              <a class="search-drawer__department-card" href="${link}">
                <span class="search-drawer__department-media">
                  ${media}
                </span>
                <span class="search-drawer__department-title">${text}</span>
              </a>
            `;
          }

          return `
            <button type="button" class="search-drawer__department-card" data-search-category-query="${this.escapeAttr(item.text)}">
              <span class="search-drawer__department-media">
                ${media}
              </span>
              <span class="search-drawer__department-title">${text}</span>
            </button>
          `;
        })
        .join('');
    }

    addRecentSearch(query) {
      const cleanQuery = (query || '').trim();
      if (!cleanQuery) return;

      const deduped = this.state.recent.filter((value) => value.toLowerCase() !== cleanQuery.toLowerCase());
      this.state.recent = [cleanQuery].concat(deduped).slice(0, 6);
      this.saveRecentSearches();
      this.renderPrimaryHistorySection();
    }

    clearHistory() {
      this.state.recent = [];
      this.saveRecentSearches();
      this.renderPrimaryHistorySection();
    }

    applyQuery(query) {
      const normalized = (query || '').trim();
      if (!normalized) return;

      if (this.input) this.input.value = normalized;
      this.state.query = normalized;
      if (this.clearBtn) this.clearBtn.hidden = false;
      this.updateFullSearchLink(normalized);
      this.performSearch(normalized, { immediate: true });
    }

    getListByTab(tab) {
      if (tab === 'collections') return this.state.results.collections || [];
      if (tab === 'blogs') return this.state.results.blogs || [];
      return this.state.results.products || [];
    }

    getTotalResultCount() {
      const products = (this.state.results.products || []).length;
      const collections = (this.state.results.collections || []).length;
      const blogs = (this.state.results.blogs || []).length;
      return products + collections + blogs;
    }

    getItemById(kind, id) {
      if (!kind || !id) return null;
      if (kind === 'product') return (this.state.results.products || []).find((item) => String(item.id) === String(id)) || null;
      if (kind === 'collection') return (this.state.results.collections || []).find((item) => String(item.id) === String(id)) || null;
      if (kind === 'blog') return (this.state.results.blogs || []).find((item) => String(item.id) === String(id)) || null;
      return null;
    }

    updateFullSearchLink(query) {
      if (!this.fullSearchLink) return;
      this.fullSearchLink.href = this.buildFullSearchUrl(query);
    }

    buildFullSearchUrl(query) {
      const url = new URL('/search', window.location.origin);
      const cleanQuery = (query || '').trim();
      if (cleanQuery) {
        url.searchParams.set('q', cleanQuery);
      }
      return `${url.pathname}${url.search}`;
    }

    readDrawerConfig() {
      const configEl = this.drawer ? this.drawer.querySelector('[data-search-drawer-config]') : null;
      if (!configEl) {
        return {
          trending: [],
          quickSearches: [],
          categories: [],
          categoryPlaceholderSvg: ''
        };
      }

      try {
        const parsed = JSON.parse(configEl.textContent || '{}');
        return {
          trending: this.normalizeConfigItems(parsed.trending, 5),
          quickSearches: this.normalizeConfigItems(parsed.quickSearches, 8),
          categories: this.normalizeConfigItems(parsed.categories, 5),
          categoryPlaceholderSvg: typeof parsed.categoryPlaceholderSvg === 'string' ? parsed.categoryPlaceholderSvg : ''
        };
      } catch (error) {
        return {
          trending: [],
          quickSearches: [],
          categories: [],
          categoryPlaceholderSvg: ''
        };
      }
    }

    normalizeConfigItems(items, limit) {
      if (!Array.isArray(items)) return [];
      return items
        .map((item) => {
          const text = item && typeof item.text === 'string' ? item.text.trim() : '';
          const link = item && typeof item.link === 'string' ? item.link.trim() : '';
          const image = item && typeof item.image === 'string' ? item.image.trim() : '';
          return { text, link, image };
        })
        .filter((item) => item.text !== '')
        .slice(0, limit || items.length);
    }

    loadRecentSearches() {
      try {
        const raw = window.localStorage ? window.localStorage.getItem(RECENT_KEY) : null;
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((value) => typeof value === 'string' && value.trim() !== '').slice(0, 6);
      } catch (error) {
        return [];
      }
    }

    saveRecentSearches() {
      try {
        if (!window.localStorage) return;
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(this.state.recent));
      } catch (error) {
        // noop
      }
    }

    refreshIcons() {
      if (window.UntamedIcons && typeof window.UntamedIcons.refresh === 'function') {
        window.UntamedIcons.refresh();
      }
    }

    animateIn(nodes, options) {
      if (typeof gsap === 'undefined') return;
      const config = options || {};
      const list = Array.from(nodes || []);
      if (!list.length) return;

      gsap.killTweensOf(list);
      gsap.fromTo(
        list,
        {
          opacity: 0,
          x: config.x || 0,
          y: config.y || 0
        },
        {
          opacity: 1,
          x: 0,
          y: 0,
          duration: 0.22,
          ease: 'power2.out',
          stagger: config.stagger || 0
        }
      );
    }

    priceForAscending(value) {
      if (typeof value !== 'number' || Number.isNaN(value)) return Number.MAX_SAFE_INTEGER;
      return value;
    }

    priceForDescending(value) {
      if (typeof value !== 'number' || Number.isNaN(value)) return -1;
      return value;
    }

    renderRatingMarkup(item) {
      if (!item || item.ratingEnabled === false || item.ratingHidden === true) {
        return '<span class="product-card-horizontal__rating product-card-horizontal__rating--hidden" aria-hidden="true"></span>';
      }

      const rating = Number.isFinite(item.ratingValue) ? Math.max(0, Math.min(5, item.ratingValue)) : 0;
      const reviews = Number.isFinite(item.reviewCount) ? Math.max(0, item.reviewCount) : 0;
      const fullStars = Math.floor(rating);
      const remainder = rating - fullStars;
      const hasHalfStar = remainder >= 0.25 && remainder < 0.75;
      const adjustedFull = remainder >= 0.75 ? Math.min(5, fullStars + 1) : fullStars;
      const emptyStars = Math.max(0, 5 - adjustedFull - (hasHalfStar ? 1 : 0));
      const ratingDisplay = String(item.ratingDisplay || 'star_rating_count');
      const ratingSize = (item.ratingSize === 'md' || item.ratingSize === 'lg') ? item.ratingSize : 'sm';
      const fontWeight = /^[0-9]{3}$/.test(String(item.ratingFontWeight || '')) ? String(item.ratingFontWeight) : '600';
      const ratingValueLabel = this.formatRatingValue(rating);
      const reviewsWord = item.reviewsLabel || (reviews === 1 ? 'review' : 'reviews');
      const reviewsLabel = `${reviews} ${reviewsWord}`;

      const stars = [];
      for (let i = 0; i < adjustedFull; i += 1) stars.push('<span class="product-card-horizontal__star product-card-horizontal__star--full">★</span>');
      if (hasHalfStar) stars.push('<span class="product-card-horizontal__star product-card-horizontal__star--half">★</span>');
      for (let i = 0; i < emptyStars; i += 1) stars.push('<span class="product-card-horizontal__star product-card-horizontal__star--empty">★</span>');

      let ratingText = '';
      if (ratingDisplay === 'star_only') {
        ratingText = '';
      } else if (ratingDisplay === 'star_rating') {
        ratingText = ratingValueLabel;
      } else if (ratingDisplay === 'star_count') {
        ratingText = reviews > 0 ? `(${reviewsLabel})` : 'No reviews';
      } else {
        ratingText = reviews > 0 ? `${ratingValueLabel} (${reviewsLabel})` : 'No reviews';
      }

      const textMarkup = ratingText
        ? `<span class="product-card-horizontal__rating-text" style="font-weight: ${fontWeight};">${this.escapeHtml(ratingText)}</span>`
        : '';
      return `
        <span class="product-card-horizontal__rating">
          <span class="product-card-horizontal__stars product-card-horizontal__stars--${ratingSize}" aria-hidden="true">${stars.join('')}</span>
          ${textMarkup}
        </span>
      `;
    }

    formatRatingValue(value) {
      const rounded = Math.round(Number(value || 0) * 10) / 10;
      return Number.isFinite(rounded) ? String(rounded).replace(/\.0$/, '') : '0';
    }

    renderSwatchOrStockState(item) {
      const swatches = Array.isArray(item.swatches) ? item.swatches.filter((value) => String(value || '').trim() !== '').slice(0, 4) : [];
      const overflow = Number.isFinite(item.swatchOverflow) ? Math.max(0, item.swatchOverflow) : 0;
      if (swatches.length > 0) {
        const swatchMarkup = swatches.map((label) => {
          const safeLabel = this.escapeAttr(label);
          const nameToken = this.escapeAttr(String(label || '').trim().toLowerCase());
          return `<span class="product-card-horizontal__swatch" title="${safeLabel}" data-color-label="${safeLabel}" data-color-name="${nameToken}"></span>`;
        }).join('');
        const overflowMarkup = overflow > 0 ? `<span class="product-card-horizontal__swatch-more">+${overflow}</span>` : '';
        return `<span class="product-card-horizontal__swatches">${swatchMarkup}${overflowMarkup}</span>`;
      }

      if (item.stockText) {
        const stockTone = this.escapeAttr(item.stockState || 'in-stock');
        return `<span class="product-card-horizontal__stock product-card-horizontal__stock--${stockTone}">${this.escapeHtml(item.stockText)}</span>`;
      }

      return '<span class="product-card-horizontal__state-spacer" aria-hidden="true"></span>';
    }

    formatCompactCount(value) {
      const count = Number(value);
      if (!Number.isFinite(count) || count < 1000) return String(Math.max(0, Math.round(count || 0)));
      const rounded = count >= 10000 ? Math.round(count / 1000) : Math.round((count / 1000) * 10) / 10;
      return `${String(rounded).replace(/\.0$/, '')}k`;
    }

    sanitizeColorToken(value) {
      const token = String(value || '').trim();
      if (token === '') return '';
      if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(token)) return token;
      if (/^[a-zA-Z\s-]{1,30}$/.test(token)) return token;
      return '';
    }

    sanitizeClassToken(value) {
      const token = String(value || '').trim().toLowerCase().replace(/[^a-z0-9_-]/g, '-');
      return token || 'default';
    }

    parseBoolean(value) {
      if (typeof value === 'boolean') return value;
      if (typeof value !== 'string') return false;
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }

    toNumber(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : NaN;
    }

    toInteger(value) {
      const parsed = parseInt(String(value || ''), 10);
      return Number.isFinite(parsed) ? parsed : NaN;
    }

    parsePrice(raw) {
      if (!raw) return null;
      const cleaned = String(raw).replace(/[^0-9.,]/g, '');
      if (!cleaned) return null;
      const normalized = cleaned.replace(/,/g, '');
      const parsed = parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    }

    readText(scope, selector) {
      const el = scope ? scope.querySelector(selector) : null;
      return el ? (el.textContent || '').trim() : '';
    }

    readAttr(scope, selector, attr) {
      const el = scope ? scope.querySelector(selector) : null;
      return el ? (el.getAttribute(attr) || '') : '';
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    escapeAttr(value) {
      return this.escapeHtml(value);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new SearchDrawer());
  } else {
    new SearchDrawer();
  }
})();
